import React from 'react';
import { ForeignActivitiesDirectItemModel } from 'forms/sections/foreign/activities/direct/types';
import { ValidationElementProps } from 'components/Form/ValidationElement/ValidationElement';
import {
  CommonOnError,
  CommonRequired,
  CommonScrollIntoView,
  CommonAddressBooks,
} from 'types/props';
import {
  foreignInterestOwnerOptions, foreignFinancialInterestOptions, foreignRealEstateOptions, foreignFinancialInterestHowAcquiredOptions,
  foreignFinancialInterestHowRelinquishedOptions,
  foreignFinancialInterestControllerOptions,
  foreignInterestCoOwnerOptions,
  companyOrOrg,
  anotherPerson,
  FINANCIAL_FOREIGN_INTEREST_VALUES,
  OTHR,
  REALESTATE,
} from 'constants/enums/foreignActivityOptions';
import ValidationElement from 'components/Form/ValidationElement';
import { ApplicableModel, CheckboxModel } from 'forms/shared/types';
import Field from 'components/Form/Field';
import { i18n } from 'config/locales';
import { isSafeInternational } from 'helpers/locations';
import PVQLocationUnknown from 'components/Form/PVQLocation/LocationUnknown';
import DateControl from 'components/Form/DateControl';
import Textarea from 'components/Form/Textarea';
import Currency from 'components/Form/Currency';
import NotApplicable from 'components/Form/NotApplicable';
import { ErrorCode } from '@redux/modules/application/reducers/error';
import { Dispatch } from 'redux';
import {
  CountryArrayModel, CountryModel, LocationModel
} from 'forms/shared/types';
import Dropdown from 'components/Form/Dropdown';
import PVQName from 'components/Form/PVQName';
import PVQLocation from 'components/Form/PVQLocation';
import Branch from 'components/Form/Branch';
import Country from 'components/Form/Country';
import CoOwners from '../Shared/CoOwners';

interface Props extends Partial<ForeignActivitiesDirectItemModel>, ValidationElementProps {
  onUpdate: (item: ForeignActivitiesDirectItemModel) => void,
  onError?: CommonOnError
  required?: CommonRequired
  scrollIntoView?: CommonScrollIntoView
  addressBooks?: CommonAddressBooks

  dispatch: Dispatch
  prefix: string
}

export default class DirectInterestPvq extends ValidationElement<Props> {
  update = (queue: Partial<ForeignActivitiesDirectItemModel>) => {
    this.props.onUpdate({
      InterestTypes: this.props.InterestTypes,
      InterestType: this.props.InterestType,
      Acquired: this.props.Acquired,
      HowAcquired: this.props.HowAcquired,
      Cost: this.props.Cost,
      CostEstimated: this.props.CostEstimated,
      Value: this.props.Value,
      ValueEstimated: this.props.ValueEstimated,
      Relinquished: this.props.Relinquished,
      RelinquishedNotApplicable: this.props.RelinquishedNotApplicable,
      Explanation: this.props.Explanation,
      CoOwners: this.props.CoOwners,
      // PVQ 35
      InterestOwnerType: this.props.InterestOwnerType,
      DependentChildrenName: this.props.DependentChildrenName,
      FinancialInterestType: this.props.FinancialInterestType,
      FinancialInterestTypeOtherExplanation: this.props.FinancialInterestTypeOtherExplanation,
      RealEstateType: this.props.RealEstateType,
      RealEstateTypeOtherExplanation: this.props.RealEstateTypeOtherExplanation,
      RealEstateTypeLocation: this.props.RealEstateTypeLocation,
      HowRealEstateAcquired: this.props.HowRealEstateAcquired,
      HowAcquiredOtherExplanation: this.props.HowAcquiredOtherExplanation,
      HasCoOwners: this.props.HasCoOwners,
      StillOwned: this.props.StillOwned,
      RelinquishedValue: this.props.RelinquishedValue,
      Country: this.props.Country,
      ForeignInterestAcquired: this.props.ForeignInterestAcquired,
      HowForeignInterest: this.props.HowForeignInterest,
      ForeignInterestOtherExplanation: this.props.ForeignInterestOtherExplanation,
      ForeignInterestPurchaseValue: this.props.ForeignInterestPurchaseValue,
      ForeignInterestStillOwned: this.props.ForeignInterestStillOwned,
      ForeignInterestCurrentValue: this.props.ForeignInterestCurrentValue,
      ForeignInterestSoldValue: this.props.ForeignInterestSoldValue,
      ForeignInterestHowRelinquished: this.props.ForeignInterestHowRelinquished,
      ForeignInterestHowRelinquishedOtherExplanation: this.props.ForeignInterestHowRelinquishedOtherExplanation,
      ForeignInterestSoldDate: this.props.ForeignInterestSoldDate,
      ForeignInterestControl: this.props.ForeignInterestControl,
      ForeignInterestControlOtherExplanation: this.props.ForeignInterestControlOtherExplanation,
      ForeignInterestRelationship: this.props.ForeignInterestRelationship,
      ForeignInterestControllerName: this.props.ForeignInterestControllerName,
      ForeignInterestCountry: this.props.ForeignInterestCountry,
      ForeignInterestControlAddress: this.props.ForeignInterestControlAddress,
      ForeignInterestAnotherControl: this.props.ForeignInterestAnotherControl,
      ForeignInterestAnotherControlExplanation: this.props.ForeignInterestAnotherControlExplanation,
      ForeignInterestRelationshipOtherExplanation: this.props.ForeignInterestRelationshipOtherExplanation,
      ForeignInterestControlCompanyName: this.props.ForeignInterestControlCompanyName,
      ForeignInterestCountryNotApplicable: this.props.ForeignInterestCountryNotApplicable,
      ForeignInterestControlAddressNotApplicable: this.props.ForeignInterestControlAddressNotApplicable,
      ForeignInterestControlAddressInUsaNotApplicable: this.props.ForeignInterestControlAddressInUsaNotApplicable,
      ...queue,
    });
  };

  updateInterestTypes = (values: CheckboxModel) => {
    const interestType = values.value;
    const selected = [...(this.props.InterestTypes?.values || [])];
    if (selected.includes(interestType)) {
      selected.splice(selected.indexOf(interestType), 1);
    } else {
      selected.push(interestType);
    }

    this.update({
      InterestTypes: { values: selected },
    });
  };

  updateInterestType = (values: ForeignActivitiesDirectItemModel['InterestType']) => {
    this.update({
      InterestType: values,
    });
  };

  updateAcquired = (values: ForeignActivitiesDirectItemModel['Acquired']) => {
    this.update({
      Acquired: values,
    });
  };

  updateHowAcquired = (values: ForeignActivitiesDirectItemModel['HowAcquired']) => {
    this.update({
      HowAcquired: values,
    });
  };

  updateCost = (values: ForeignActivitiesDirectItemModel['Cost']) => {
    this.update({
      Cost: values,
    });
  };

  updateCostEstimated = (values: ForeignActivitiesDirectItemModel['CostEstimated']) => {
    this.update({
      CostEstimated: values,
    });
  };

  updateValue = (values: ForeignActivitiesDirectItemModel['Value']) => {
    this.update({
      Value: values,
    });
  };

  updateValueEstimated = (values: ForeignActivitiesDirectItemModel['ValueEstimated']) => {
    this.update({
      ValueEstimated: values,
    });
  };

  updateRelinquished = (values: ForeignActivitiesDirectItemModel['Relinquished']) => {
    this.update({
      Relinquished: values,
    });
  };

  updateRelinquishedNotApplicable = (
    values: ForeignActivitiesDirectItemModel['RelinquishedNotApplicable']
  ) => {
    this.update({
      RelinquishedNotApplicable: values,
      Relinquished: values.applicable ? this.props.Relinquished : {},
      Explanation: values.applicable ? this.props.Explanation : {},
    });
  };

  updateExplanation = (values: ForeignActivitiesDirectItemModel['Explanation']) => {
    this.update({
      Explanation: values,
    });
  };

  updateCoOwners = (values: ForeignActivitiesDirectItemModel['CoOwners']) => {
    this.update({
      CoOwners: values,
    });
  };

  // pvq updates

  updateInterestOwnerType = (values: ForeignActivitiesDirectItemModel['InterestOwnerType']) => {
    this.update({
      InterestOwnerType: values,
      DependentChildrenName: values.value !== 'Dependent children' ? this.props.DependentChildrenName : {},
    });
  };

  updateDependentChildrenName = (values: ForeignActivitiesDirectItemModel['DependentChildrenName']) => {
    this.update({
      DependentChildrenName: values,
    });
  };

  updateFinancialInterestType = (values: ForeignActivitiesDirectItemModel['FinancialInterestType']) => {
    this.update({
      FinancialInterestType: values,
    });
  };

  updateFinancialInterestTypeOtherExplanation = (values: ForeignActivitiesDirectItemModel['FinancialInterestTypeOtherExplanation']) => {
    this.update({
      FinancialInterestTypeOtherExplanation: values,
    });
  };

  updateRealEstateType = (values: ForeignActivitiesDirectItemModel['RealEstateType']) => {
    this.update({
      RealEstateType: values,
    });
  };

  updateRealEstateTypeOtherExplanation = (values: ForeignActivitiesDirectItemModel['RealEstateTypeOtherExplanation']) => {
    this.update({
      RealEstateTypeOtherExplanation: values,
    });
  };

  updateRealEstateTypeLocation = (values: ForeignActivitiesDirectItemModel['RealEstateTypeLocation']) => {
    this.update({
      RealEstateTypeLocation: values,
    });
  };

  updateHowRealEstateAcquired = (values: ForeignActivitiesDirectItemModel['HowRealEstateAcquired']) => {
    this.update({
      HowRealEstateAcquired: values,
    });
  };

  updateHowAcquiredOtherExplanation = (values: ForeignActivitiesDirectItemModel['HowAcquiredOtherExplanation']) => {
    this.update({
      HowAcquiredOtherExplanation: values,
    });
  };

  updateStillOwned = (values: ForeignActivitiesDirectItemModel['StillOwned']) => {
    this.update({
      StillOwned: values,
    });
  };

  updateRelinquishedValue = (values: ForeignActivitiesDirectItemModel['RelinquishedValue']) => {
    this.update({
      RelinquishedValue: values,
    });
  };

  updateCountry = (values: ForeignActivitiesDirectItemModel['Country']) => {
    this.update({
      Country: values,
    });
  };

  updateForeignInterestAcquired = (values: ForeignActivitiesDirectItemModel['ForeignInterestAcquired']) => {
    this.update({
      ForeignInterestAcquired: values,
    });
  };

  updateHowForeignInterest = (values: ForeignActivitiesDirectItemModel['HowForeignInterest']) => {
    this.update({
      HowForeignInterest: values,
    });
  };

  updateForeignInterestOtherExplanation = (values: ForeignActivitiesDirectItemModel['ForeignInterestOtherExplanation']) => {
    this.update({
      ForeignInterestOtherExplanation: values,
    });
  };

  updateForeignInterestPurchaseValue = (values: ForeignActivitiesDirectItemModel['ForeignInterestPurchaseValue']) => {
    this.update({
      ForeignInterestPurchaseValue: values,
    });
  };

  updateForeignInterestStillOwned = (values: ForeignActivitiesDirectItemModel['ForeignInterestStillOwned']) => {
    this.update({
      ForeignInterestStillOwned: values,
    });
  };

  updateForeignInterestCurrentValue = (values: ForeignActivitiesDirectItemModel['ForeignInterestCurrentValue']) => {
    this.update({
      ForeignInterestCurrentValue: values,
    });
  };

  updateForeignInterestSoldValue = (values: ForeignActivitiesDirectItemModel['ForeignInterestSoldValue']) => {
    this.update({
      ForeignInterestSoldValue: values,
    });
  };

  updateForeignInterestHowRelinquished = (values: ForeignActivitiesDirectItemModel['ForeignInterestHowRelinquished']) => {
    this.update({
      ForeignInterestHowRelinquished: values,
    });
  };

  updateForeignInterestHowRelinquishedOtherExplanation = (values: ForeignActivitiesDirectItemModel['ForeignInterestHowRelinquishedOtherExplanation']) => {
    this.update({
      ForeignInterestHowRelinquishedOtherExplanation: values,
    });
  };

  updateForeignInterestSoldDate = (values: ForeignActivitiesDirectItemModel['ForeignInterestSoldDate']) => {
    this.update({
      ForeignInterestSoldDate: values,
    });
  };

  updateForeignInterestControl = (values: ForeignActivitiesDirectItemModel['ForeignInterestControl']) => {
    this.update({
      ForeignInterestControl: values,
    });
  };

  updateForeignInterestControlOtherExplanation = (values: ForeignActivitiesDirectItemModel['ForeignInterestControlOtherExplanation']) => {
    this.update({
      ForeignInterestControlOtherExplanation: values,
    });
  };

  updateForeignInterestRelationship = (values: ForeignActivitiesDirectItemModel['ForeignInterestRelationship']) => {
    this.update({
      ForeignInterestRelationship: values,
    });
  };

  updateForeignInterestControllerName = (values: ForeignActivitiesDirectItemModel['ForeignInterestControllerName']) => {
    this.update({
      ForeignInterestControllerName: values,
    });
  };

  updateForeignInterestCountry = (values: ForeignActivitiesDirectItemModel['ForeignInterestCountry']) => {
    this.update({
      ForeignInterestCountry: values,
    });
  };

  updateForeignInterestControlAddress = (values: ForeignActivitiesDirectItemModel['ForeignInterestControlAddress']) => {
    this.update({
      ForeignInterestControlAddress: values,
    });
  };

  updateForeignInterestAnotherControl = (values: ForeignActivitiesDirectItemModel['ForeignInterestAnotherControl']) => {
    this.update({
      ForeignInterestAnotherControl: values,
    });
  };

  updateForeignInterestAnotherControlExplanation = (values: ForeignActivitiesDirectItemModel['ForeignInterestAnotherControlExplanation']) => {
    this.update({
      ForeignInterestAnotherControlExplanation: values,
    });
  };

  updateForeignInterestControlCompanyName = (values: ForeignActivitiesDirectItemModel['ForeignInterestControlCompanyName']) => {
    this.update({
      ForeignInterestControlCompanyName: values,
    });
  };

  updateForeignInterestRelationshipOtherExplanation = (values: ForeignActivitiesDirectItemModel['ForeignInterestRelationshipOtherExplanation']) => {
    this.update({
      ForeignInterestRelationshipOtherExplanation: values,
    });
  };

  updateHasCoOwners = (values: ForeignActivitiesDirectItemModel['HasCoOwners']) => {
    this.update({
      HasCoOwners: values,
    });
  };

  updateForeignInterestControlAddressNotApplicable = (
    foreignInterestControlAddressNotApplicable: ForeignActivitiesDirectItemModel['ForeignInterestControlAddressNotApplicable']
  ) => {
    const queue: Partial<ForeignActivitiesDirectItemModel> = {
      ForeignInterestControlAddressNotApplicable: foreignInterestControlAddressNotApplicable
    };

    if (foreignInterestControlAddressNotApplicable?.applicable === false) {
      const address: LocationModel | undefined = this.props.ForeignInterestControlAddress;

      let country: CountryModel | string | CountryArrayModel | undefined = address?.country;

      if (country) {
        if (isSafeInternational(address)) {
          country = { value: 'INTERNATIONAL' } as CountryModel;
        }

        queue.ForeignInterestControlAddress = { country } as LocationModel;
      } else {
        queue.ForeignInterestControlAddress = {} as LocationModel;
      }
    }

    this.update(queue);
  };

  updateForeignInterestControlAddressInUsaNotApplicable = (
    ForeignInterestControlAddressInUsaNotApplicable: ApplicableModel
  ) => {
    const queue: Partial<ForeignActivitiesDirectItemModel> = {
      ForeignInterestControlAddressInUsaNotApplicable: { applicable: ForeignInterestControlAddressInUsaNotApplicable.applicable }
    };

    if (!ForeignInterestControlAddressInUsaNotApplicable.applicable) {
      queue.ForeignInterestControlAddress = {} as LocationModel;
    }

    this.update(queue);
  };

  updateForeignInterestCountryNotApplicable = (
    values: ForeignActivitiesDirectItemModel['ForeignInterestCountryNotApplicable']
  ) => {
    this.update({
      ForeignInterestCountry: values.applicable ? this.props.ForeignInterestCountry : {},
      ForeignInterestCountryNotApplicable: values,
    });
  };

  render() {
    const { prefix } = this.props;
    const attribute = `List.items[${this.props.$index}].Item`;

    const labelPrefix = 'foreign.pvq.activities.direct.financialInterest';
    const associationPrefix = 'foreign.pvq.activities.financialInterest';

    const isOther = (value: string | undefined) => value === 'Other';

    return (
      // Who ownes foreign.pvq.activities.direct.financialInterest.label
      <div className="interest-owner">
        <Field
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.InterestOwnerType`}
          title={i18n.t('foreign.pvq.activities.direct.interest.primary.type')}
        >
          <Dropdown
            attribute={`${attribute}.InterestOwnerType`}
            name="interest-owner-type"
            onError={this.props.onError}
            onUpdate={this.updateInterestOwnerType}
            value={this.props.InterestOwnerType?.value}
            required={this.props.required}
          >
            {foreignInterestOwnerOptions.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </Dropdown>
        </Field>
        {/*  when child show name */}
        {this.props.InterestOwnerType?.value === 'Dependent children' && (
          <Field
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.DependentChildrenName`}
          >
            <PVQName
              name="dependent-children-name"
              {...this.props.DependentChildrenName}
              onUpdate={this.updateDependentChildrenName}
              title={i18n.t('foreign.pvq.activities.financialInterest.dependentChild')}
              onError={this.handleNameError}
              required={this.props.required}
              scrollIntoView={this.props.scrollIntoView}
              attribute={`${attribute}.DependentChildrenName`}
              showLastFirstMiddleNameOrder

            />
          </Field>
        )}
        <Field>
          <Branch
            name="has-coowner"
            attribute={`${attribute}.HasCoOwners`}
            labelSize="h4"
            className="has-coowner no-margin-bottom"
            {...this.props.HasCoOwners}
            onUpdate={this.updateHasCoOwners}
            onError={this.props.onError}
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
            label={i18n.t(`foreign.pvq.activities.direct.heading.cotitle`)}
          />
          {this.props.HasCoOwners?.value === 'Yes' && (
            <CoOwners
              prefix={prefix}
              {...this.props.CoOwners}
              addressBooks={this.props.addressBooks}
              dispatch={this.props.dispatch}
              onUpdate={this.updateCoOwners}
              onError={this.props.onError}
              required={this.props.required}
              scrollIntoView={this.props.scrollIntoView}
              attribute={`${attribute}.CoOwners`}
              peopleWhoKnowYou={this.props.peopleWhoKnowYou}
              formType={this.props.formType}
            />
          )}
        </Field>
        <Field
          // What is the foreign financial interest you [...] own or owned?
          title={i18n.m('foreign.pvq.activities.financialInterest.question')}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.FinancialInterestType`}
        >
          <Dropdown
            attribute={`${attribute}.FinancialInterestType`}
            name="financial-interest-type"
            onError={this.props.onError}
            onUpdate={this.updateFinancialInterestType}
            value={this.props.FinancialInterestType?.value}
            required={this.props.required}
          >
            {foreignFinancialInterestOptions.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </Dropdown>
        </Field>
        {this.props.FinancialInterestType?.value === OTHR && (
          <Field
            // Please explain.
            title={i18n.t('foreign.pvq.activities.financialInterest.explain')}
            attribute={`${attribute}.FinancialInterestTypeOtherExplanation`}
          >
            <Textarea
              name="FinancialInterestTypeOtherExplanation"
              className="financial-interest-type-explanation"
              {...this.props.FinancialInterestTypeOtherExplanation}
              onUpdate={this.updateFinancialInterestTypeOtherExplanation}
              onError={this.handleError}
              required={this.props.required}
              attribute={`${attribute}.FinancialInterestTypeOtherExplanation`}
            />
          </Field>
        )}
        {this.props.FinancialInterestType?.value === REALESTATE && (
          <>
            <Field
              scrollIntoView={this.props.scrollIntoView}
              attribute={`${attribute}.RealEstateType`}
              title={i18n.t(`${associationPrefix}.realEstate.type`)}
            >
              <Dropdown
                attribute={`${attribute}.RealEstateType`}
                name="realestate-type"
                onError={this.props.onError}
                onUpdate={this.updateRealEstateType}
                value={this.props.RealEstateType?.value}
                required={this.props.required}
              >
                {foreignRealEstateOptions.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </Dropdown>
            </Field>
            {isOther(this.props.RealEstateType?.value) && (
              <Field
                title={i18n.t('foreign.pvq.activities.financialInterest.explain')}
                scrollIntoView={this.props.scrollIntoView}
                attribute={`${attribute}.RealEstateTypeOtherExplanation`}
              >
                <Textarea
                  {...this.props.RealEstateTypeOtherExplanation}
                  name="RealEstateTypeOtherExplanation"
                  onUpdate={this.updateRealEstateTypeOtherExplanation}
                  onError={this.props.onError}
                  className="real-estate-other-explanation"
                  required={this.props.required}
                  attribute={`${attribute}.RealEstateTypeOtherExplanation`}
                />
              </Field>
            )}
            <Field
              scrollIntoView={this.props.scrollIntoView}
              attribute={`${attribute}.RealEstateTypeLocation`}
              title={i18n.t(`${associationPrefix}.realEstate.location.label`)}
            >
              <PVQLocation
                {...this.props.RealEstateTypeLocation}
                name="RealEstateTypeLocation"
                onUpdate={this.updateRealEstateTypeLocation}
                onError={this.props.onError}
                className="RealEstateTypeLocation"
                showInternationalStreet
                geocode
                addressBooks={this.props.addressBooks}
                layout={PVQLocation.STREET_CITY_COUNTRY}
                addressBook="ForeignNational"
                domesticTitle={i18n.t(`${associationPrefix}.realEstate.location.label`)}
                foreignTitle={i18n.t(`${associationPrefix}.realEstate.location.label`)}
                dispatch={this.props.dispatch}
                required={this.props.required}
                attribute={`${attribute}.RealEstateTypeLocation`}
                showDNK
              />
            </Field>
            <Field
              title={i18n.t(`${associationPrefix}.realEstate.acquisition.acquisitionDate`)}
              adjustFor="labels"
              scrollIntoView={this.props.scrollIntoView}
              attribute={`${attribute}.Acquired`}
            >
              <DateControl
                name="RealEstateAcquired"
                className="acquired"
                minDateEqualTo
                {...this.props.Acquired}
                onUpdate={this.updateAcquired}
                onError={this.props.onError}
                required={this.props.required}
                attribute={`${attribute}.Acquired`}
                hideDay
                showEstimated
              />
            </Field>
            <Field
              title={i18n.t(`${associationPrefix}.realEstate.acquisition.acquisitionHow`)}
              adjustFor="labels"
              scrollIntoView={this.props.scrollIntoView}
              attribute={`${attribute}.HowRealEstateAcquired`}
            >
              <Dropdown
                attribute={`${attribute}.HowRealEstateAcquired`}
                name="how-acquired"
                onError={this.props.onError}
                onUpdate={this.updateHowRealEstateAcquired}
                value={this.props.HowRealEstateAcquired?.value}
                required={this.props.required}
              >
                {foreignFinancialInterestHowAcquiredOptions.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </Dropdown>
              {isOther(this.props.HowRealEstateAcquired?.value) && (
                <Field
                  title={i18n.t('foreign.pvq.activities.financialInterest.explain')}
                  adjustFor="labels"
                  scrollIntoView={this.props.scrollIntoView}
                  attribute={`${attribute}.HowAcquiredOtherExplanation`}
                >
                  <Textarea
                    name="HowAcquiredOtherExplanation"
                    className="how-acquired-explanation"
                    {...this.props.HowAcquiredOtherExplanation}
                    onUpdate={this.updateHowAcquiredOtherExplanation}
                    onError={this.props.onError}
                    required={this.props.required}
                    attribute={`${attribute}.HowAcquiredOtherExplanation`}
                  />
                </Field>
              )}
            </Field>
            <Field
              title={i18n.t(`${associationPrefix}.association.cost`)}
              adjustFor="labels"
              scrollIntoView={this.props.scrollIntoView}
              attribute={`${attribute}.Cost`}
            >
              <Currency
                name="Cost"
                className="cost"
                {...this.props.Cost}
                onUpdate={this.updateCost}
                min="0"
                onError={this.props.onError}
                required={this.props.required}
                attribute={`${attribute}.Cost`}
              />
            </Field>
            <Branch
              name="still-owned"
              label={i18n.t('foreign.pvq.activities.financialInterest.realEstate.acquisition.currentOwnership')}
              attribute={`${attribute}.StillOwned`}
              labelSize="h4"
              className="still-owned no-margin-bottom"
              {...this.props.StillOwned}
              onUpdate={this.updateStillOwned}
              onError={this.props.onError}
              required={this.props.required}
              scrollIntoView={this.props.scrollIntoView}
            />
            {this.props.StillOwned?.value === 'Yes' && (
              <Field
                title={i18n.t('foreign.pvq.activities.financialInterest.realEstate.acquisition.value')}
                scrollIntoView={this.props.scrollIntoView}
                attribute={`${attribute}.Value`}
              >
                <Currency
                  name="CurrentRealEstateValue"
                  className="value"
                  {...this.props.Value}
                  onUpdate={this.updateValue}
                  min="0"
                  onError={this.props.onError}
                  required={this.props.required}
                  attribute={`${attribute}.Value`}
                />
              </Field>
            )}
            {this.props.StillOwned?.value === 'No' && (
              <>
                <Field
                  title={i18n.t('foreign.pvq.activities.financialInterest.realEstate.acquisition.relinquishedValue')}
                  scrollIntoView={this.props.scrollIntoView}
                  attribute={`${attribute}.RelinquishedValue`}
                >
                  <Currency
                    name="RelinquishedValue"
                    className="RelinquishedValue"
                    {...this.props.RelinquishedValue}
                    onUpdate={this.updateRelinquishedValue}
                    min="0"
                    onError={this.props.onError}
                    required={this.props.required}
                    attribute={`${attribute}.RelinquishedValue`}
                  />
                </Field>
                <Field
                  title={i18n.t('foreign.pvq.activities.financialInterest.realEstate.acquisition.relinquishedDate')}
                  scrollIntoView={this.props.scrollIntoView}
                  attribute={`${attribute}.Relinquished`}
                >
                  <DateControl
                    name="relinquished-date"
                    className="relinquished"
                    minDateEqualTo
                    {...this.props.Relinquished}
                    onUpdate={this.updateRelinquished}
                    onError={this.props.onError}
                    required={this.props.required}
                    attribute={`${attribute}.Relinquished`}
                    hideDay
                    showEstimated
                  />
                </Field>
              </>
            )}
          </>
        )}
        {FINANCIAL_FOREIGN_INTEREST_VALUES.includes(this.props.FinancialInterestType?.value || '') && (
          <>
            <Field
              title={i18n.t('foreign.pvq.activities.financialInterest.association.country')}
              adjustFor="country"
              scrollIntoView={this.props.scrollIntoView}
              attribute={`${attribute}.Country`}
            >
              <Country
                name="Country"
                {...this.props.Country}
                onUpdate={this.updateCountry}
                onError={this.props.onError}
                className="Country"
                required={this.props.required}
                attribute={`${attribute}.Country`}
              />
            </Field>
            <Field
              title={i18n.t('foreign.pvq.activities.financialInterest.association.acquisitionDate')}
              adjustFor="datecontrol"
              scrollIntoView={this.props.scrollIntoView}
              attribute={`${attribute}.ForeignInterestAcquired`}
            >
              <DateControl
                name="ForeignInterestAcquired"
                {...this.props.ForeignInterestAcquired}
                onUpdate={this.updateForeignInterestAcquired}
                onError={this.props.onError}
                className="foreign-aquired-date"
                required={this.props.required}
                hideDay
                attribute={`${attribute}.ForeignInterestAcquired`}
              />
            </Field>
            <Field
              title={i18n.t('foreign.pvq.activities.financialInterest.association.label')}
              scrollIntoView={this.props.scrollIntoView}
              attribute={`${attribute}.HowForeignInterest`}
            >
              <Dropdown
                attribute={`${attribute}.HowForeignInterest`}
                name="foreign-how-acquired"
                onError={this.props.onError}
                onUpdate={this.updateHowForeignInterest}
                value={this.props.HowForeignInterest?.value}
                required={this.props.required}
              >
                {foreignFinancialInterestHowAcquiredOptions.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </Dropdown>
            </Field>
            {isOther(this.props.HowForeignInterest?.value) && (
              <Field
                title={i18n.t('foreign.pvq.activities.financialInterest.explain')}
                scrollIntoView={this.props.scrollIntoView}
                attribute={`${attribute}.ForeignInterestOtherExplanation`}
              >
                <Textarea
                  name="ForeignInterestOtherExplanation"
                  className="foreign-acquired-explanation"
                  {...this.props.ForeignInterestOtherExplanation}
                  onUpdate={this.updateForeignInterestOtherExplanation}
                  onError={this.props.onError}
                  required={this.props.required}
                  attribute={`${attribute}.ForeignInterestOtherExplanation`}
                />
              </Field>
            )}
            <Field
              title={i18n.t('foreign.pvq.activities.financialInterest.association.cost')}
              scrollIntoView={this.props.scrollIntoView}
              attribute={`${attribute}.ForeignInterestPurchaseValue`}
            >
              <Currency
                name="ForeignInterestPurchaseValue"
                className="financial-ForeignInterestPurchaseValue"
                {...this.props.ForeignInterestPurchaseValue}
                onUpdate={this.updateForeignInterestPurchaseValue}
                min="0"
                onError={this.props.onError}
                required={this.props.required}
                attribute={`${attribute}.ForeignInterestPurchaseValue`}
              />
            </Field>
            <Branch
              name="foreign-interest-still-owned"
              attribute={`${attribute}.ForeignInterestStillOwned`}
              label={i18n.t('foreign.pvq.activities.financialInterest.association.currentOwnership')}
              labelSize="h4"
              className="foreign-interest-still-owned no-margin-bottom"
              {...this.props.ForeignInterestStillOwned}
              onUpdate={this.updateForeignInterestStillOwned}
              onError={this.props.onError}
              required={this.props.required}
              scrollIntoView={this.props.scrollIntoView}
            />
            {this.props.ForeignInterestStillOwned?.value === 'Yes' && (
              <Field
                title={i18n.t('foreign.pvq.activities.financialInterest.association.currentValue')}
                scrollIntoView={this.props.scrollIntoView}
                attribute={`${attribute}.ForeignInterestCurrentValue`}
              >
                <Currency
                  name="ForeignInterestCurrentValue"
                  className="financial-interest-current"
                  {...this.props.ForeignInterestCurrentValue}
                  onUpdate={this.updateForeignInterestCurrentValue}
                  min="0"
                  onError={this.props.onError}
                  required={this.props.required}
                  attribute={`${attribute}.ForeignInterestCurrentValue`}
                />
              </Field>
            )}
            {this.props.ForeignInterestStillOwned?.value === 'No' && (
              <>
                <Field
                  title={i18n.t('foreign.pvq.activities.financialInterest.association.relinquishedValue')}
                  scrollIntoView={this.props.scrollIntoView}
                  attribute={`${attribute}.ForeignInterestSoldValue`}
                >
                  <Currency
                    name="ForeignInterestSoldValue"
                    className="ForeignInterestSoldValue"
                    {...this.props.ForeignInterestSoldValue}
                    onUpdate={this.updateForeignInterestSoldValue}
                    min="0"
                    onError={this.props.onError}
                    required={this.props.required}
                    attribute={`${attribute}.ForeignInterestSoldValue`}
                  />
                </Field>
                <Field
                  title="How was this financial interest relinquished?"
                  scrollIntoView={this.props.scrollIntoView}
                  attribute={`${attribute}.ForeignInterestHowRelinquished`}
                >
                  <Dropdown
                    attribute={`${attribute}.ForeignInterestHowRelinquished`}
                    name="ForeignInterestHowRelinquished"
                    onError={this.props.onError}
                    onUpdate={this.updateForeignInterestHowRelinquished}
                    value={this.props.ForeignInterestHowRelinquished?.value}
                    required={this.props.required}
                  >
                    {foreignFinancialInterestHowRelinquishedOptions.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </Dropdown>
                </Field>
                {isOther(this.props.ForeignInterestHowRelinquished?.value) && (
                  <Field
                    title={i18n.t('foreign.pvq.activities.financialInterest.explain')}
                  >
                    <Textarea
                      name="ForeignInterestHowRelinquishedOtherExplanation"
                      className="ForeignInterestHowRelinquishedOtherExplanation"
                      {...this.props.ForeignInterestHowRelinquishedOtherExplanation}
                      onUpdate={this.updateForeignInterestHowRelinquishedOtherExplanation}
                      onError={this.props.onError}
                      required={this.props.required}
                      attribute={`${attribute}.ForeignInterestHowRelinquishedOtherExplanation`}
                    />
                  </Field>
                )}
                <Field
                  title="When was this foreign financial interest relinquished?"
                  adjustFor="labels"
                  scrollIntoView={this.props.scrollIntoView}
                  attribute={`${attribute}.ForeignInterestSoldDate`}
                >
                  <DateControl
                    name="ForeignInterestSoldDate"
                    className="ForeignInterestSoldDate"
                    minDateEqualTo
                    hideDay
                    {...this.props.ForeignInterestSoldDate}
                    onUpdate={this.updateForeignInterestSoldDate}
                    onError={this.props.onError}
                    required={this.props.required}
                    attribute={`${attribute}.ForeignInterestSoldDate`}
                  />
                </Field>
              </>
            )}
            <Field
              title={i18n.t('foreign.pvq.activities.financialInterest.association.relinquishedMethod.org')}
            >
              <Dropdown
                attribute={`${attribute}.ForeignInterestControl`}
                name="ForeignInterestControl"
                onError={this.props.onError}
                onUpdate={this.updateForeignInterestControl}
                value={this.props.ForeignInterestControl?.value}
                required={this.props.required}
              >
                {foreignFinancialInterestControllerOptions.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </Dropdown>
              {isOther(this.props.ForeignInterestControl?.value) && (
                <Field
                  title={i18n.t('foreign.pvq.activities.financialInterest.explain')}
                >
                  <Textarea
                    name="ForeignInterestControlOtherExplanation"
                    className="ForeignInterestControlOtherExplanation"
                    {...this.props.ForeignInterestControlOtherExplanation}
                    onUpdate={this.updateForeignInterestControlOtherExplanation}
                    onError={this.props.onError}
                    required={this.props.required}
                    attribute={`${attribute}.ForeignInterestControlOtherExplanation`}
                  />
                </Field>
              )}
            </Field>
            {this.props.ForeignInterestControl?.value === companyOrOrg && (
              <Field
                title={i18n.t('foreign.pvq.activities.financialInterest.association.companyName')}
              >
                <Textarea
                  name="ForeignInterestControlCompanyName"
                  className="ForeignInterestControlCompanyName"
                  {...this.props.ForeignInterestControlCompanyName}
                  onUpdate={this.updateForeignInterestControlCompanyName}

                  onError={this.props.onError}
                  required={this.props.required}
                  attribute={`${attribute}.ForeignInterestControlCompanyName`}
                />
              </Field>
            )}
            {this.props.ForeignInterestControl?.value === anotherPerson && (
              <>
                <Field
                  title={i18n.t('foreign.pvq.activities.financialInterest.association.orgRelationship')}
                  attribute={`${attribute}.ForeignInterestRelationship`}
                >
                  <Dropdown
                    attribute={`${attribute}.ForeignInterestRelationship`}
                    name="foreign-interest-relationship"
                    onError={this.props.onError}
                    onUpdate={this.updateForeignInterestRelationship}
                    value={this.props.ForeignInterestRelationship?.value}
                    required={this.props.required}
                  >
                    {foreignInterestCoOwnerOptions.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </Dropdown>
                </Field>
                {isOther(this.props.ForeignInterestRelationship?.value) && (
                  <Field
                    title={i18n.t('foreign.pvq.activities.financialInterest.explain')}
                  >
                    <Textarea
                      name="ForeignInterestRelationshipOtherExplanation"
                      className="ForeignInterestRelationshipOtherExplanation"
                      {...this.props.ForeignInterestRelationshipOtherExplanation}
                      onUpdate={this.updateForeignInterestRelationshipOtherExplanation}
                      onError={this.props.onError}
                      required={this.props.required}
                      attribute={`${attribute}.ForeignInterestRelationshipOtherExplanation`}
                    />
                  </Field>
                )}
                <Field
                  // What is the name of the person who controls the foreign financial interest?
                  title={i18n.t('foreign.pvq.activities.financialInterest.association.orgName')}
                  scrollIntoView={this.props.scrollIntoView}
                  attribute={`${attribute}.ForeignInterestControllerName`}
                >
                  <PVQName
                    name="ForeignInterestControllerName"
                    {...this.props.ForeignInterestControllerName}
                    onUpdate={this.updateForeignInterestControllerName}
                    onError={this.handleNameError}
                    required={this.props.required}
                    scrollIntoView={this.props.scrollIntoView}
                    attribute={`${attribute}.ForeignInterestControllerName`}
                    showLastFirstMiddleNameOrder
                  />
                </Field>
                <Field
                  title={i18n.t('foreign.pvq.activities.financialInterest.association.citizenship')}
                  adjustFor="country"
                  scrollIntoView={this.props.scrollIntoView}
                  attribute={`${attribute}.ForeignInterestCountry`}
                >
                  <NotApplicable
                    {...this.props.ForeignInterestCountryNotApplicable}
                    name="ForeignInterestCountryNotApplicable"
                    className="foreign-country-not-applicable"
                    onUpdate={this.updateForeignInterestCountryNotApplicable}
                    onError={this.props.onError}
                    or={i18n.m('foreign.pvq.business.contact.para.or')}
                    label={i18n.t('foreign.pvq.business.contact.label.idk')}
                    required={this.props.required}
                    attribute={`${attribute}.ForeignInterestCountryNotApplicable`}
                    includeNAConfirmation={Boolean(this.props.ForeignInterestCountry?.value)}
                  >
                    <Country
                      name="ForeignInterestCountry"
                      {...this.props.ForeignInterestCountry}
                      onUpdate={this.updateForeignInterestCountry}
                      onError={this.props.onError}
                      className="ForeignInterestCountry"
                      multiple
                      required={this.props.required}
                      attribute={`${attribute}.ForeignInterestCountry`}
                    />
                  </NotApplicable>
                </Field>
                <Field
                  scrollIntoView={this.props.scrollIntoView}
                  attribute={`${attribute}.ForeignInterestControlAddress`}
                >
                  <PVQLocationUnknown
                    {...this.props.ForeignInterestControlAddress}
                    addressUnknown={this.props.ForeignInterestControlAddressNotApplicable}
                    addressUnknownLabel={i18n.t('foreign.pvq.contacts.label.idk')}
                    inUsaUnknown={this.props.ForeignInterestControlAddressInUsaNotApplicable}
                    inUsaUnknownLabel={i18n.t('foreign.pvq.contacts.label.idk')}
                    attribute={`${attribute}.ForeignInterestControlAddress`}
                    name="foreign-national-address"
                    onUpdate={this.updateForeignInterestControlAddress} // eslint-disable-line @typescript-eslint/unbound-method
                    onUpdateAddressUnknown={this.updateForeignInterestControlAddressNotApplicable}
                    onUpdateInUsaUnknown={this.updateForeignInterestControlAddressInUsaNotApplicable}
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                    onError={this.props.onError}
                    required={this.props.required}
                    geocode
                    showInUsaUnknown
                    showAddressUnknown
                    showCityUnknown
                    showInternationalStreet
                    showIsInstallationUnknownIfDomestic
                    showIsInstallationUnknownIfInternational
                    showMilitaryInstallation
                    hideInstallationNameUnknownWhenForeign
                    showApoFpoUnknown
                    showStreetUnknown
                    showInternationalStreetUnknown
                    showZipcodeUnknown
                    label={i18n.t('foreign.pvq.activities.financialInterest.association.unknownLabel')}
                    domesticTitle={i18n.t(`foreign.pvq.contacts.heading.addressDomesticTitle`)}
                    foreignTitle={i18n.t(`foreign.pvq.contacts.heading.addressForeignTitle`)}
                  />
                </Field>
              </>
            )}
            {(this.props.ForeignInterestControl?.value === anotherPerson
              || this.props.ForeignInterestControl?.value === companyOrOrg) && (
                <>
                  <Field
                    title={i18n.t('foreign.pvq.activities.financialInterest.association.orgQuestion')}
                  >
                    <Branch
                      name="still-owned-another-control"
                      attribute={`${attribute}.ForeignInterestAnotherControl`}
                      title={i18n.t(`${labelPrefix}.controlled`)}
                      labelSize="h4"
                      className="still-owned no-margin-bottom"
                      {...this.props.ForeignInterestAnotherControl}
                      onUpdate={this.updateForeignInterestAnotherControl}
                      onError={this.props.onError}
                      required={this.props.required}
                      scrollIntoView={this.props.scrollIntoView}
                    />
                  </Field>
                  {this.props.ForeignInterestAnotherControl?.value === 'Yes' && (
                    <Field
                      title={i18n.t('foreign.pvq.activities.financialInterest.explain')}
                      scrollIntoView={this.props.scrollIntoView}
                      attribute={`${attribute}.ForeignInterestAnotherControlExplanation`}
                    >
                      <Textarea
                        name="ForeignInterestAnotherControlExplanation"
                        className="ForeignInterestAnotherControlExplanation"
                        {...this.props.ForeignInterestAnotherControlExplanation}
                        onUpdate={this.updateForeignInterestAnotherControlExplanation}
                        onError={this.props.onError}
                        required={this.props.required}
                        attribute={`${attribute}.ForeignInterestAnotherControlExplanation`}
                      />
                    </Field>
                  )}
                </>
            )}
          </>
        )}
      </div>
    );
  }
}

DirectInterestPvq.defaultProps = {
  prefix: 'activities.direct.interest',
  addressBooks: {},
  Relenquished: {},
  RelinquishedNotApplicable: { applicable: true },
  applicantBirthdate: {},
  dispatch: () => { },
  onUpdate: () => { },
  onError: (value, arr: ErrorCode) => arr,
  InterestOwnerType: {},
  DependentChildrenName: {},
  FinancialInterestType: {},
  FinancialInterestTypeOtherExplanation: {},
  RealEstateType: {},
  RealEstateTypeOtherExplanation: {},
  RealEstateTypeLocation: {},
  HowRealEstateAcquired: {},
  HowAcquiredOtherExplanation: {},
  HasCoOwners: {},
  StillOwned: {},
  RelinquishedValue: {},
  Country: {},
  ForeignInterestAcquired: {},
  HowForeignInterest: {},
  ForeignInterestOtherExplanation: {},
  ForeignInterestPurchaseValue: {},
  ForeignInterestStillOwned: {},
  ForeignInterestCurrentValue: {},
  ForeignInterestSoldValue: {},
  ForeignInterestHowRelinquished: {},
  ForeignInterestHowRelinquishedOtherExplanation: {},
  ForeignInterestSoldDate: {},
  ForeignInterestControl: {},
  ForeignInterestControlOtherExplanation: {},
  ForeignInterestRelationship: {},
  ForeignInterestControllerName: {},
  ForeignInterestCountry: {},
  ForeignInterestControlAddress: {},
  ForeignInterestAnotherControl: {},
  ForeignInterestAnotherControlExplanation: {},
  ForeignInterestControlCompanyName: {},
  ForeignInterestCountryNotApplicable: { applicable: true },
  ForeignInterestControlAddressNotApplicable: { applicable: true },
  ForeignInterestControlAddressInUsaNotApplicable: { applicable: true },
  ForeignInterestRelationshipOtherExplanation: {},
};
